import ClearAllRoundedIcon from '@mui/icons-material/ClearAllRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { Alert, Button, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import * as Immutable from 'immutable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import { ClearCompletedProductPublishJobs, ClearFailedProductPublishJobs } from '../../../UserData/_actions/Store/StoreProductPublishJobActions';
import { FetchStoreData } from '../../../UserData/_actions/Store/StoreActions';
import type ProductPublishJobStore from '../../../UserData/_stores/Store/ProductPublish/ProductPublishJobStore';
import type { ProductPublishJobStatus } from '../../../UserData/_stores/Store/ProductPublish/ProductPublishJobStore';
import type UserStoreStore from '../../../UserData/_stores/Store/UserStoreStore';
import ProductPublishJobList from '../AddProductListing/ProductPublishJobList';

const ACTIVE_PRODUCT_PUBLISH_STATUSES: ProductPublishJobStatus[] = ['pending', 'processing']
const FINISHED_PRODUCT_PUBLISH_STATUSES: ProductPublishJobStatus[] = ['published', 'failed', 'cancelled']
const CLEARABLE_COMPLETED_PRODUCT_PUBLISH_STATUSES: ProductPublishJobStatus[] = ['published', 'cancelled']

const getProductPublishNoticeState = (
	jobs?: Immutable.List<ProductPublishJobStore>
) => {
	const activeBatchIds = new Set<string>()
	const state = {
		active: 0,
		clearableCompleted: 0,
		clearableFailed: 0,
		failed: 0,
		published: 0,
		publishingDone: 0,
		publishingTotal: 0,
		retryable: 0,
		visibleJobCount: jobs?.count() ?? 0,
	}

	jobs?.forEach(job => {
		if (ACTIVE_PRODUCT_PUBLISH_STATUSES.includes(job.get('status'))) {
			state.active++
			activeBatchIds.add(job.get('batch_id'))
		}

		if (job.get('status') === 'failed') {
			state.failed++
			state.clearableFailed++

			if (job.get('can_retry')) {
				state.retryable++
			}
		}

		if (job.get('status') === 'published') {
			state.published++
		}

		if (CLEARABLE_COMPLETED_PRODUCT_PUBLISH_STATUSES.includes(job.get('status'))) {
			state.clearableCompleted++
		}
	})

	if (activeBatchIds.size > 0) {
		jobs?.forEach(job => {
			if (!activeBatchIds.has(job.get('batch_id'))) {
				return
			}

			state.publishingTotal++

			if (FINISHED_PRODUCT_PUBLISH_STATUSES.includes(job.get('status'))) {
				state.publishingDone++
			}
		})
	}

	return state
}

export const isStorePublishingProducts = (store?: UserStoreStore) => {
	const productPublishJobs = store?.get('product_publish_summary').get('jobs')
	const productPublishNoticeState = getProductPublishNoticeState(productPublishJobs)

	return productPublishNoticeState.active > 0 || (store?.get('is_adding_products') ?? 0) > 0
}

type Props = {
	store: UserStoreStore
}

export default function ProductPublishNotice(props: Props) {
	const dispatch = useAppDispatch()
	const [t] = useTranslation('account-v2')
	const [publishDetailsOpened, setPublishDetailsOpened] = useState(false)

	const productPublishJobs = props.store.get('product_publish_summary').get('jobs')
	const productPublishNoticeState = useMemo(() => (
		getProductPublishNoticeState(productPublishJobs)
	), [productPublishJobs])
	const isAddingProducts = productPublishNoticeState.active > 0 || props.store.get('is_adding_products') > 0
	const publishingTotal = productPublishNoticeState.publishingTotal || props.store.get('is_adding_products')
	const publishingProgressLabel = isAddingProducts && publishingTotal > 0
		? t('Publishing {{value}} of {{total}} products', {
			value: Math.min(productPublishNoticeState.publishingDone + 1, Math.max(publishingTotal, 1)),
			total: Math.max(publishingTotal, 1),
		})
		: t('Publishing {{count}} products to your store...', { count: props.store.get('is_adding_products') })
	const failedPublishLabel = productPublishNoticeState.retryable > 0
		? t('{{count}} products failed to publish. {{retryable}} can be retried below.', {
			count: productPublishNoticeState.failed,
			retryable: productPublishNoticeState.retryable,
		})
		: t('{{count}} products failed to publish. View details below.', {
			count: productPublishNoticeState.failed,
		})
	const hasProductPublishNotice = isAddingProducts || productPublishNoticeState.visibleJobCount > 0
	const publishedPublishLabel = productPublishNoticeState.published === 1
		? t('1 product published to your store.')
		: t('{{count}} products published to your store.', { count: productPublishNoticeState.published })
	const productPublishNoticeLabel = isAddingProducts
		? publishingProgressLabel
		: (productPublishNoticeState.failed > 0 ? failedPublishLabel : publishedPublishLabel)

	const onPublishDetailsToggle = useCallback(() => setPublishDetailsOpened(value => !value), [])

	const onClearCompletedProductPublishJobs = useCallback(() => {
		dispatch(ClearCompletedProductPublishJobs(props.store.get('slug')).set({
			onSuccess: () => dispatch(FetchStoreData(props.store.get('slug'))),
			onFailure: () => dispatch(FetchStoreData(props.store.get('slug'))),
		}))
	}, [dispatch, props.store])

	const onClearFailedProductPublishJobs = useCallback(() => {
		dispatch(ClearFailedProductPublishJobs(props.store.get('slug')).set({
			onSuccess: () => dispatch(FetchStoreData(props.store.get('slug'))),
			onFailure: () => dispatch(FetchStoreData(props.store.get('slug'))),
		}))
	}, [dispatch, props.store])

	useEffect(() => {
		if (!hasProductPublishNotice) {
			setPublishDetailsOpened(false)
		}
	}, [hasProductPublishNotice])

	if (!hasProductPublishNotice) {
		return null
	}

	return <Alert
		severity={productPublishNoticeState.failed > 0 ? 'warning' : (isAddingProducts ? 'info' : 'success')}
		variant="outlined"
		icon={isAddingProducts ? <CircularProgress color="inherit" size={20} /> : undefined}
		sx={{
			mb: 2,

			'& .MuiAlert-message': {
				width: '100%',
				pt: 0.5,
				pb: 0.25,
			},
		}}
	>
		<Stack spacing={1.25} sx={{ width: '100%' }}>
			<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
				<Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0 }}>
					{productPublishNoticeLabel}
				</Typography>
				<Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 0.5 }}>
					{!isAddingProducts && productPublishNoticeState.clearableFailed > 0 ? <Button
						color="inherit"
						size="small"
						startIcon={<ClearAllRoundedIcon />}
						onClick={onClearFailedProductPublishJobs}
					>{t('Clear failed')}</Button> : null}
					{!isAddingProducts && productPublishNoticeState.clearableCompleted > 0 ? <Button
						color="inherit"
						size="small"
						startIcon={<ClearAllRoundedIcon />}
						onClick={onClearCompletedProductPublishJobs}
					>{t('Clear completed')}</Button> : null}
					{productPublishNoticeState.visibleJobCount > 0 ? <Button
						color="inherit"
						size="small"
						endIcon={publishDetailsOpened ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
						onClick={onPublishDetailsToggle}
					>{publishDetailsOpened ? t('Hide details') : t('View details')}</Button> : null}
				</Stack>
			</Stack>
			<Collapse in={publishDetailsOpened && productPublishNoticeState.visibleJobCount > 0} timeout="auto" unmountOnExit>
				<ProductPublishJobList store={props.store.get('slug')} />
			</Collapse>
		</Stack>
	</Alert>
}
